import type { CaptureStatus, DesktopBridge, Shareable } from "@typedoc/contracts";
import type { CaptureShare } from "@typedoc/contracts/capture";

/**
 * A desktop shell in the page's memory, for the sample island's scenes about what Abacor sees
 * (ENG-1031): `share` asks before it starts with a display chosen, `share-apps` with the apps the
 * person used last time, and `not-shared` is already recording Chrome and Excel when the person
 * moves into Slack. Nothing is captured; the shell only answers as a real one would.
 */
export const SHARE_SCENES = ["share", "share-apps", "not-shared"] as const;
export type ShareScene = (typeof SHARE_SCENES)[number];

export const isShareScene = (scene: string): scene is ShareScene =>
  (SHARE_SCENES as readonly string[]).includes(scene);

const CHROME = { bundleId: "com.google.Chrome", name: "Google Chrome" };
const EXCEL = { bundleId: "com.microsoft.Excel", name: "Microsoft Excel" };
const SLACK = { bundleId: "com.tinyspeck.slackmacgap", name: "Slack" };

/** How long after recording starts the not-shared scene moves into Slack. */
const INTO_SLACK_MS = 1_200;

const svg = (body: string, width: number, height: number) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${String(width)}" height="${String(height)}" viewBox="0 0 ${String(width)} ${String(height)}">${body}</svg>`,
  )}`;

/** A small picture of a desktop with a few windows on it, as a display's thumbnail. */
function desktopPicture(windows: string, from: string, to: string) {
  return svg(
    `<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${from}"/><stop offset="1" stop-color="${to}"/></linearGradient></defs><rect width="480" height="300" fill="url(#g)"/>${windows}`,
    480,
    300,
  );
}

const BUILT_IN = desktopPicture(
  `<g filter="drop-shadow(0 4px 6px rgba(0,0,0,0.12))"><rect x="38" y="42" width="298" height="192" rx="8" fill="#fff"/><rect x="38" y="42" width="48" height="192" rx="8" fill="#22303A"/><rect x="72" y="42" width="14" height="192" fill="#22303A"/><rect x="269" y="114" width="173" height="132" rx="8" fill="#fff"/><path d="M277 114h157a8 8 0 0 1 8 8v13H269v-13a8 8 0 0 1 8-8z" fill="#1F6E43"/></g>`,
  "#E9E4DB",
  "#CFC8BC",
);
const EXTERNAL = desktopPicture(
  `<g filter="drop-shadow(0 4px 6px rgba(0,0,0,0.12))"><rect x="48" y="48" width="336" height="198" rx="8" fill="#fff"/><path d="M56 48h320a8 8 0 0 1 8 8v18H48V56a8 8 0 0 1 8-8z" fill="#2F6FB3"/><rect x="148" y="74" width="1" height="172" fill="#ECE9E3"/></g>`,
  "#D8D4CC",
  "#BDB6AA",
);

/** An app's icon: a rounded tile with its letter, or Chrome's rings. */
function tile(fill: string, letter: string) {
  return svg(
    `<rect width="64" height="64" rx="16" fill="${fill}"/><text x="32" y="43" text-anchor="middle" font-family="system-ui, sans-serif" font-size="30" font-weight="700" fill="#fff">${letter}</text>`,
    64,
    64,
  );
}
const CHROME_ICON = svg(
  `<rect width="64" height="64" rx="16" fill="#fff"/><circle cx="32" cy="32" r="22" fill="#DB4437"/><path d="M32 32 L51.05 43 A22 22 0 0 1 12.95 43 Z" fill="#0F9D58"/><path d="M32 32 L12.95 43 A22 22 0 0 1 32 10 Z" fill="#F4B400"/><circle cx="32" cy="32" r="10" fill="#fff"/><circle cx="32" cy="32" r="7.5" fill="#4285F4"/>`,
  64,
  64,
);

export const SAMPLE_SHAREABLE: Omit<Shareable, "remembered"> = {
  displays: [
    {
      id: 1,
      name: "Built-in display",
      width: 1512,
      height: 982,
      apps: ["Chrome", "Excel"],
      thumbnail: BUILT_IN,
    },
    {
      id: 2,
      name: "LG UltraFine",
      width: 2560,
      height: 1440,
      apps: ["Outlook", "Slack"],
      thumbnail: EXTERNAL,
    },
  ],
  // Front to back, as the shell lists them; the island puts the browsers first.
  apps: [
    { ...EXCEL, icon: tile("#1F7A46", "X"), window: "Fuel index 2026.xlsx", windows: 1 },
    { ...CHROME, icon: CHROME_ICON, window: "Load 4471 · Carrier charges", windows: 2 },
    {
      bundleId: "com.microsoft.Outlook",
      name: "Microsoft Outlook",
      icon: tile("#1F5FAE", "O"),
      window: "Inbox · Northline invoices",
      windows: 1,
    },
    {
      bundleId: "com.apple.Safari",
      name: "Safari",
      icon: tile("#1B7CF2", "S"),
      window: "Northline · Rate card",
      windows: 1,
    },
    { ...SLACK, icon: tile("#4A154B", "S"), window: "#ops-carriers", windows: 1 },
  ],
  working: 1,
};

type Capture = Pick<
  DesktopBridge,
  | "bridgeVersion"
  | "startCapture"
  | "setCaptureGrant"
  | "pauseCapture"
  | "resumeCapture"
  | "stopCapture"
  | "shareableContent"
  | "setCaptureShare"
  | "keepOutOfCapture"
  | "onCaptureStatus"
>;

export function createSampleDesktop(scene: ShareScene): Capture {
  const listeners = new Set<(status: CaptureStatus | null) => void>();
  const kept = new Set<string>();
  const shared: CaptureShare = { kind: "apps", apps: [CHROME, EXCEL] };
  // The not-shared scene is already recording when the island opens.
  let status: CaptureStatus | null =
    scene === "not-shared" ? { share: shared, outside: null } : null;
  const tell = (next: CaptureStatus | null) => {
    status = next;
    for (const listener of listeners) listener(next);
  };
  const inSlack = () => {
    if (!status || status.outside) return;
    const sharesSlack =
      status.share?.kind === "apps" && status.share.apps.some((a) => a.bundleId === SLACK.bundleId);
    if (sharesSlack) return;
    tell({
      share: status.share,
      outside: {
        app: SLACK.name,
        bundleId: SLACK.bundleId,
        offer: status.share?.kind === "apps" && !kept.has(SLACK.bundleId),
      },
    });
  };

  return {
    // A shell that covers card fields as well as password fields (ENG-1026).
    bridgeVersion: 6,
    startCapture: async (start) => {
      if (start.share) tell({ share: start.share, outside: null });
      else if (!status) tell({ share: shared, outside: null });
      if (scene === "not-shared") setTimeout(inSlack, INTO_SLACK_MS);
      return { started: true };
    },
    setCaptureGrant: () => {},
    pauseCapture: () => {},
    resumeCapture: () => {},
    stopCapture: () => tell(null),
    shareableContent: async () => ({
      ...SAMPLE_SHAREABLE,
      remembered:
        scene === "share-apps"
          ? { kind: "apps", bundleIds: [CHROME.bundleId, EXCEL.bundleId] }
          : null,
    }),
    setCaptureShare: (share) => {
      if (!status) return;
      const back =
        status.outside?.bundleId &&
        share.kind === "apps" &&
        share.apps.some((a) => a.bundleId === status?.outside?.bundleId);
      tell({ share, outside: back ? null : status.outside });
    },
    keepOutOfCapture: (bundleId) => {
      kept.add(bundleId);
      if (status?.outside?.bundleId === bundleId) {
        tell({ ...status, outside: { ...status.outside, offer: false } });
      }
    },
    onCaptureStatus: (listener) => {
      listeners.add(listener);
      // As the shell does: what is shared now, first.
      queueMicrotask(() => {
        if (listeners.has(listener)) listener(status);
      });
      return () => listeners.delete(listener);
    },
  };
}
